/**
 * Pick the airport from `runways.json` whose lat/lon is closest to the
 * given point (typically the user's station). Used to default
 * `$currentIcao` so the right rail loads relevant flights/weather/NOTAMs
 * for the airport you actually feed, instead of the hardcoded RJTT.
 *
 * Pure helper so it can be unit-tested without DOM / store / network.
 */
export interface AirportLatLon {
  icao: string;
  lat: number;
  lon: number;
}

/** Great-circle distance in km via the haversine formula. */
function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function nearestAirport(
  station: { lat: number; lon: number },
  airports: AirportLatLon[],
): AirportLatLon | null {
  let best: { ap: AirportLatLon; d: number } | null = null;
  for (const ap of airports) {
    if (typeof ap.lat !== "number" || typeof ap.lon !== "number") continue;
    const d = haversineKm(station, ap);
    if (!best || d < best.d) best = { ap, d };
  }
  return best?.ap ?? null;
}

/** Convenience: extract `{icao, lat, lon}` records from the bundled
 * runways.json shape `{ "RJTT": {icao, lat, lon, ...}, ... }`. */
export function extractAirports(runwaysData: Record<string, unknown>): AirportLatLon[] {
  const out: AirportLatLon[] = [];
  for (const [key, val] of Object.entries(runwaysData)) {
    if (key.startsWith("_")) continue;
    if (typeof val !== "object" || val === null) continue;
    const v = val as { icao?: string; lat?: number; lon?: number };
    if (typeof v.lat === "number" && typeof v.lon === "number") {
      out.push({ icao: v.icao ?? key, lat: v.lat, lon: v.lon });
    }
  }
  return out;
}
